<script context="module">
  import { get } from "svelte/store";
  import { authenticatedFetch, currentUser } from "@stores/auth";
  import { featureEnabled } from "@stores/features";
  import { currentMode, userHasPaid } from "@stores/features/paywall";
  import { experience } from "@stores/global";
  import { showPaywall } from "@stores/sheets";

  export const DEFAULT = "default";
  export const SIGN_IN = "sign-in";
  export const PAYMENT = "payment";

  /**
   * Checks if payment is required for experience
   * If not, prevents paywall being displayed
   *
   * @returns {Promise<boolean>}
   */
   export function checkForExistingOrder() {
    return requiresPayment().then((required) => {
      if (!required) {
        showPaywall.set(false);
        userHasPaid.set(true);
      }

      return required;
    });
  }

  /**
   * If user not signed in, or user has not paid for the experience
   * display paywall
   *
   * @returns {Promise<boolean>} overlayRequired
   */
  export async function requiresPayment() {
    if (!featureEnabled("paywall")) {
      return false;
    }

    if (get(userHasPaid)) {
      return false;
    }

    if (!get(currentUser)) {
      return true;
    }

    let userOwnsExperience = false;
    try {
      ({ userOwnsExperience } = await authenticatedFetch(
        `/experience/${get(experience).id}/payment-status`
      ));
    } catch (error) {
      return false;
    }

    if (userOwnsExperience && !get(userHasPaid)) {
      userHasPaid.set(true);
    }

    return !userOwnsExperience;
  }

  /**
   * Opens paywall on payment form (or sign in form if not authed)
   */
  export function openPaymentForm() {
    showPaywall.set(true);
    currentMode.set(PAYMENT);
  }

  /**
   * Opens paywall in default state
   */
  export function openPaywall() {
    showPaywall.set(true);
    currentMode.set(DEFAULT);
  }
</script>

<script>
  import { onMount } from "svelte";
  import I18nHtml from "@components/I18nHtml";
  import PaymentForm from "@components/PaymentForm";
  import SignInForm, {
    REGISTER_MODE,
    SIGN_IN_MODE
  } from "@components/SignInForm";
  import Sheet, { SHEET_POSITIONS } from "@components/Sheet";
  import { _, number } from "@services/i18n";
  import { getStartIntent } from "@services/speakeasy";
  import { getConfiguration } from "@stores/component-configurations";
  import { featureProperties } from "@stores/features";
  import { escapeTags, toSentenceCase } from "@utils/string";

  $: signedIn = Boolean($currentUser);

  let dismissing = false;

  let required = false;

  let signInFormMode = SIGN_IN_MODE;

  const defaultSignedInContent = escapeTags([
    // eslint-disable-next-line max-len, quotes
    '<p><a href="#" data-on="click|preventDefault:dismiss" title="Free encounter">FREE ENCOUNTER</a></p>',
    // eslint-disable-next-line max-len, quotes
    '<p><a href="#" data-on="click|preventDefault:buy" title="Buy full access pass">FULL ACCESS PASS {price}</a></p>'
  ]);

  const defaultSignedOutContent = escapeTags([
    // eslint-disable-next-line max-len, quotes
    '<p>First time in the room? Access the <a href="#" data-on="click|preventDefault:dismiss" title="Free encounter">FREE ENCOUNTER</a></p>',
    // eslint-disable-next-line max-len, quotes
    '<p><a href="#" data-on="click|preventDefault:buy" title="Buy full access pass">FULL ACCESS PASS {price}</a></p>',
    // eslint-disable-next-line max-len, quotes
    '<p><a href="#" data-on="click|preventDefault:signIn" title="Buy full access pass">SIGN IN</a> (for passholders)</p>'
  ]);

  const {amount, currency} = featureProperties("paywall");

  const formattedPrice = $number(amount / 100, {
    style: "currency",
    currency: currency || "gbp"
  });

  const {
    signedInContent,
    signedOutContent,
    title: defaultTitle
  } = getConfiguration(
    "paywall",
    {
      title: $_("components.paywall.default_title", { default: "Welcome!" }),
      signedInContent: $_("components.paywall.signed_in.html", {
        default: defaultSignedInContent,
        values: {
          price: formattedPrice
        }
      }),
      signedOutContent: $_("components.paywall.signed_out.html", {
        default: defaultSignedOutContent,
        values: {
          price: formattedPrice
        }
      }, {
        ignoreTags: false
      })
    }
  );

  let signInFormTitle = $_("components.paywall.default_sign_in_form_title", {
    default: "Sign in"
  });
  let title = defaultTitle;

  $: {
    if ($currentMode === DEFAULT) {
      title = defaultTitle;
    }

    if ($currentMode === SIGN_IN) {
      title = signInFormTitle;
    }

    if ($currentMode === PAYMENT) {
      if ($currentUser) {
        title = $_("components.paywall.default_payment_form_title", {
          default: "Checkout"
        });
      } else {
        title = toSentenceCase(signInFormTitle);
        register();
      }
    }
  }

  /**
   * Load start intent and dismiss overlay
   */
  async function dismiss() {
    if (dismissing === false) {
      try {
        dismissing = true;
        await getStartIntent($experience.id);
        showPaywall.set(false);
        // eslint-disable-next-line require-atomic-updates
        dismissing = false;
      } catch (e) {
        // eslint-disable-next-line require-atomic-updates
        dismissing = false;
      }
    }
  }

  /**
   * Show welcome
   */
  function welcome() {
    $currentMode = DEFAULT;
  }

  /**
   * Show sign in form in register mode
   */
  function register() {
    $currentMode = SIGN_IN;
    signInFormMode = REGISTER_MODE;
  }

  /**
   * Show sign in form
   */
  function signIn() {
    $currentMode = SIGN_IN;
    signInFormMode = SIGN_IN_MODE;
  }

  /**
   * Show payment form
   */
  function buy() {
    $currentMode = PAYMENT;
  }

  /**
   * Re-checks if we need to show paywall &
   * resets state back to default
   */
  async function reset() {
    const stillRequired = await requiresPayment();

    if (stillRequired) {
      if ($currentUser) {
        buy();
      } else {
        welcome();
      }
    } else {
      dismiss();
      userHasPaid.set(true);
      required = false;
    }
  }

  onMount(async () => {
    required = await checkForExistingOrder();
  });
</script>

<style>
  .content :global(p:last-child) {margin: 0}
</style>

{#if required}
  <Sheet position={SHEET_POSITIONS.CENTER}  hideTitle={!title.length}>
    <span slot="title">
      {title}
    </span>

    {#if $currentMode === SIGN_IN}
      <SignInForm bind:mode={signInFormMode} bind:title={signInFormTitle} on:close={welcome} on:success={reset} />
    {:else if $currentMode === PAYMENT}
      <PaymentForm {amount} {currency} on:close={welcome} on:success={reset} />
    {:else}
      <div class="content">
        {#if signedIn}
          <I18nHtml
            content={signedInContent}
            on:buy={buy}
            on:dismiss={dismiss}
            price={formattedPrice}
          />
        {:else}
          <I18nHtml
            content={signedOutContent}
            on:buy={buy}
            on:dismiss={dismiss}
            on:register={register}
            on:signIn={signIn}
            price={formattedPrice}
          />
        {/if}
      </div>
    {/if}
  </Sheet>
{/if}
